import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from "@heroicons/react/24/solid";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { SortDirection } from "./useTableData";
import { twMerge } from "tailwind-merge";
import { UseTableDataReturn } from "./useTableData";
import { useMemo } from "react";
import Loading from "../Loading";

export interface ColumnProps<T> {
  title: string;
  convertToCellData: (item: T) => React.ReactNode;
  colSpan?: number;
  width?: string;
  disabled?: boolean;
  sortKey?: keyof T;
  defaultSort?: boolean;
}

export interface TableProps<T> {
  tableData: UseTableDataReturn<T>;
  rowId: (item: T) => string;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  columns: ColumnProps<T>[];
  trailingColumnKey?: keyof T & string;
  headerColor?: boolean;
  className?: string;
  headerClass?: string;
}

export const Table = <T,>({
  tableData,
  rowId,
  onEdit,
  onDelete,
  columns,
  trailingColumnKey,
  className = "",
  headerClass = "",
}: TableProps<T>) => {
  const hasEditOrDelete = useMemo(() => onEdit || onDelete, [onEdit, onDelete]);
  const hasTrailingActions = !!trailingColumnKey;

  const {
    items,
    totalPages,
    currentPage,
    setCurrentPage,
    sortField,
    sortDirection,
    handleSortClick,
    loading,
  } = tableData;

  const renderSortIcon = (field: keyof T | undefined) => {
    if (!field) return null;

    if (sortField !== field) {
      return <ChevronUpDownIcon className="w-4 h-4 text-gray-500 transition-opacity" />;
    }
    return sortDirection === SortDirection.ASC
      ? <ChevronUpIcon className="w-4 h-4 text-gray-700" />
      : <ChevronDownIcon className="w-4 h-4 text-gray-700" />;
  };

  return (
    <div className={twMerge("overflow-hidden", className)}>
      <div className="hidden lg:block overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full border-collapse">
          <thead>
            <tr className={twMerge("bg-gradient-to-r from-gray-50 to-gray-100/80", headerClass)}>
              {columns.map((column) => {
                const isActive = sortField === column.sortKey;
                const isSortable = !!column.sortKey;

                return (
                  <th
                    key={column.title}
                    onClick={() => column.sortKey && handleSortClick(column.sortKey)}
                    className={twMerge(
                      "text-left px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 group border-b border-gray-200",
                      isSortable && "cursor-pointer hover:text-gray-700 transition-colors",
                      isActive && "text-gray-700 bg-gray-50/40"
                    )}
                    style={{ width: column.width }}
                  >
                    <div className="flex items-center gap-2">
                      <span>{column.title}</span>
                      {renderSortIcon(column.sortKey)}
                    </div>
                  </th>
                );
              })}
              {hasEditOrDelete && (
                <th className="text-left px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  Actions
                </th>
              )}
              {hasTrailingActions && (
                <th className="w-px px-2 py-4 border-b border-gray-200" aria-label="Row actions" />
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (hasEditOrDelete ? 1 : 0) + (hasTrailingActions ? 1 : 0)} className="py-16">
                  <div className="flex justify-center items-center">
                    <Loading message="Loading..." borderColor="border-brand-500" />
                  </div>
                </td>
              </tr>
            ) : items?.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (hasEditOrDelete ? 1 : 0) + (hasTrailingActions ? 1 : 0)} className="px-6 py-14 text-center">
                  <div className="text-gray-400">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-500 mb-1">No records found</p>
                    <p className="text-xs text-gray-400">Try adjusting your filters or check back later.</p>
                  </div>
                </td>
              </tr>
            ) : (
              items?.map((item: T, rowIndex: number) => (
                <tr
                  key={rowId(item) + rowIndex}
                  className={twMerge(
                    "transition-colors duration-150 border-b border-gray-100 last:border-b-0",
                    rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.title}
                      className="px-5 py-4 text-sm text-gray-700"
                    >
                      {column.convertToCellData(item)}
                    </td>
                  ))}
                  {hasEditOrDelete && (
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {onEdit && (
                          <button
                            onClick={(e) => { e.preventDefault(); onEdit(item); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors border border-brand-200/50"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                            Edit
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={(e) => { e.preventDefault(); onDelete(item); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200/50"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                  {hasTrailingActions && (item as Record<string, unknown>)[trailingColumnKey] != null && (
                    <td className="px-2 py-4 text-right align-middle">
                      {(item as Record<string, unknown>)[trailingColumnKey] as React.ReactNode}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <div className="flex justify-center items-center py-16 bg-white rounded-xl border border-gray-200">
            <Loading message="Loading..." borderColor="border-brand-500" />
          </div>
        ) : items?.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 px-6 py-12 text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">No records found</p>
            <p className="text-xs text-gray-400">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          items?.map((item: T, index: number) => (
            <div
              key={rowId(item) + index}
              className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="space-y-3">
                {columns.map(column => (
                  <div key={column.title} className="flex flex-col">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                      {column.title}
                    </span>
                    <span className="text-sm text-gray-700">
                      {column.convertToCellData(item)}
                    </span>
                  </div>
                ))}
              </div>

              {hasTrailingActions && (item as Record<string, unknown>)[trailingColumnKey!] != null && (
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                  {(item as Record<string, unknown>)[trailingColumnKey!] as React.ReactNode}
                </div>
              )}

              {hasEditOrDelete && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  {onEdit && (
                      <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onEdit(item);
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDelete(item);
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="inline-flex items-center gap-1 bg-white rounded-xl shadow-sm border border-gray-200 p-1.5">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-9 h-9 text-gray-500 bg-white rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
              title="First page"
            >
              <ChevronDoubleLeftIcon className="w-4 h-4" />
            </button>

            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-9 h-9 text-gray-500 bg-white rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
              title="Previous page"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>

            <div className="flex items-center px-1">
              {(() => {
                const maxPagesToShow = 5;
                let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                let endPage = startPage + maxPagesToShow - 1;

                if (endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(1, endPage - maxPagesToShow + 1);
                }

                const pageButtons = [];

                if (startPage > 1) {
                  pageButtons.push(
                    <PageButton key={1} page={1} currentPage={currentPage} setCurrentPage={setCurrentPage} />,
                    <EllipsisButton key="start-ellipsis" />
                  );
                }

                for (let page = startPage; page <= endPage; page++) {
                  pageButtons.push(
                    <PageButton key={page} page={page} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                  );
                }

                if (endPage < totalPages) {
                  pageButtons.push(
                    <EllipsisButton key="end-ellipsis" />,
                    <PageButton key={totalPages} page={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                  );
                }

                return pageButtons;
              })()}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-9 h-9 text-gray-500 bg-white rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
              title="Next page"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>

            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-9 h-9 text-gray-500 bg-white rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
              title="Last page"
            >
              <ChevronDoubleRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

type PageButtonProps = {
  page: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const PageButton = ({ page, currentPage, setCurrentPage }: PageButtonProps) => {
  const isActive = page === currentPage;

  return (
    <button
      onClick={() => setCurrentPage(page)}
      className={twMerge(
        "w-9 h-9 text-sm font-medium rounded-lg transition-all",
        isActive
          ? "bg-brand-600 text-white shadow-sm"
          : "text-gray-600 hover:bg-gray-100"
      )}
    >
      {page}
    </button>
  );
}

const EllipsisButton = () => {
  return (
    <span className="px-2 text-sm text-gray-400 select-none">
      …
    </span>
  );
}
